import { postUsersInvitationsByTokenCodeRedeem } from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Label, toast } from "@win-reality/react-ui";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAppContext } from "@/hooks/use-app-context";
import { ROUTES } from "@/lib/routes";

const ClaimPermissionsPage = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token") || "";

  const [tokenInput, setTokenInput] = useState(tokenFromUrl);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAppContext();

  const redeemMutation = useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await postUsersInvitationsByTokenCodeRedeem({
        path: {
          token_code: token,
        },
      });

      if (error) {
        throw new Error(
          `This invitation link is invalid or has expired.
           Please verify your token and try again.
           If you believe this is an error, contact support.`,
        );
      }

      return data;
    },
    onSuccess: async () => {
      // Wait for the new org roles, or the redirect lands on No Access again
      await queryClient.invalidateQueries();

      toast("Permissions claimed successfully!", {
        variant: "success",
        duration: 4000,
      });

      navigate(ROUTES.BASE);
    },
    onError: (error: Error) => {
      toast(error.message || "Failed to claim permissions", {
        variant: "error",
        duration: 6000,
      });
    },
  });

  const handleClaim = () => {
    if (!tokenInput.trim()) {
      toast("Please enter a valid token", { variant: "error", duration: 4000 });
      return;
    }

    if (!hasConfirmed) {
      setHasConfirmed(true);
      return;
    }

    redeemMutation.mutate(tokenInput.trim());
  };

  const handleCancel = () => {
    setHasConfirmed(false);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] p-6">
      <div className="w-full max-w-md space-y-6 bg-background p-8 rounded-lg shadow-lg">
        <div className="space-y-2 text-center">
          <p className="text-lg">
            {!hasConfirmed
              ? "Enter your invitation token to claim permissions for your account."
              : "Confirm you want to claim these permissions."}
          </p>
        </div>

        {!hasConfirmed ? (
          <div className="space-y-4">
            {user && (
              <div className="space-y-2">
                <Label className="text-sm">Account</Label>
                <p className="text-sm text-text-light">
                  {user.emails?.[0] || "Unknown"}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="token" className="text-sm">
                Invitation Token
              </Label>
              <Input
                id="token"
                type="text"
                placeholder="Enter your invitation token"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                isDisabled={redeemMutation.isPending}
              />
              <p className="text-xs text-accent-dark-3">
                This token was provided in your invitation link or email.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary-flat-light"
                onClick={() => navigate(ROUTES.DASHBOARD)}
                isDisabled={redeemMutation.isPending}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                onClick={handleClaim}
                isDisabled={!tokenInput.trim() || redeemMutation.isPending}
                isLoading={redeemMutation.isPending}
                className="flex-1"
              >
                Next
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-500/50 rounded p-4">
              <p className="text-sm text-blue-900 font-medium mb-2">
                Important:
              </p>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>This token can only be redeemed once</li>
                <li>You cannot undo this action</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary-flat-light"
                onClick={handleCancel}
                isDisabled={redeemMutation.isPending}
                className="flex-1"
              >
                Go Back
              </Button>
              <Button
                variant="secondary-flat-light"
                onClick={handleClaim}
                isDisabled={redeemMutation.isPending}
                isLoading={redeemMutation.isPending}
                className="flex-1"
              >
                Confirm & Claim
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimPermissionsPage;
